// Firebase configuration - dynamically loaded from backend
import { initializeApp, getApps, deleteApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

const API_URL = process.env.REACT_APP_BACKEND_URL;

let firebaseApp = null;
let messaging = null;
let auth = null;
let _configCache = null;

/**
 * Fetch Firebase config from backend (public endpoint).
 * Returns null if Firebase is not configured/enabled.
 */
export async function fetchFirebaseConfig() {
  if (_configCache !== null) return _configCache;
  try {
    const resp = await fetch(`${API_URL}/api/auth/firebase-config`);
    if (!resp.ok) return null;
    const data = await resp.json();
    if (!data.enabled || !data.apiKey) {
      _configCache = null;
      return null;
    }
    _configCache = data;
    return data;
  } catch {
    return null;
  }
}

/** Clear the config cache (useful after admin updates config). */
export function clearFirebaseConfigCache() {
  _configCache = null;
  // Re-initialize on next call
  if (firebaseApp) {
    try { deleteApp(firebaseApp); } catch {}
    firebaseApp = null;
    auth = null;
    messaging = null;
  }
}

function initApp(config) {
  if (firebaseApp) return firebaseApp;
  const fbConfig = {
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
    storageBucket: config.storageBucket,
    messagingSenderId: config.messagingSenderId,
    appId: config.appId,
  };
  firebaseApp = getApps()[0] || initializeApp(fbConfig);
  return firebaseApp;
}

export function getFirebaseApp() {
  return firebaseApp;
}

/**
 * Sign in with Google using Firebase popup.
 * Returns the Firebase ID token to send to backend.
 */
export async function signInWithGoogle() {
  const config = await fetchFirebaseConfig();
  if (!config) throw new Error('Firebase Google Auth is not configured. Please contact your administrator.');
  const app = initApp(config);
  auth = getAuth(app);
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');
  const result = await signInWithPopup(auth, provider);
  const idToken = await result.user.getIdToken();
  return {
    idToken,
    email: result.user.email,
    name: result.user.displayName,
    picture: result.user.photoURL,
  };
}

export async function getFirebaseMessaging() {
  if (messaging) return messaging;
  try {
    const supported = await isSupported();
    if (!supported) return null;
    const config = await fetchFirebaseConfig();
    if (!config) return null;
    const app = initApp(config);
    messaging = getMessaging(app);
    return messaging;
  } catch {
    return null;
  }
}

export async function requestFCMToken() {
  try {
    const msg = await getFirebaseMessaging();
    if (!msg) return null;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey || vapidKey === 'PLACEHOLDER_VAPID_KEY') {
      return null;
    }

    const token = await getToken(msg, { vapidKey });
    return token;
  } catch {
    return null;
  }
}

export function onFCMMessage(callback) {
  getFirebaseMessaging().then((msg) => {
    if (msg) {
      onMessage(msg, (payload) => {
        callback(payload);
      });
    }
  });
}

export async function isFirebaseConfigured() {
  const config = await fetchFirebaseConfig();
  return config !== null;
}
